import React, {useEffect, useRef, useState} from "react";
import IdItem from "../../models/interfaces/generics/id-item.ts";
import Card from "../../components/card";
import SimpleLoadingIndicator from "../../components/simple-loading-indicator";
import IdNameItem from "@/models/interfaces/generics/id-name-item.ts";

export interface CardLayoutProps<T extends IdItem> {
    cards: T[];
    Component: React.ComponentType<T>;
}

type InViewMountProps = {
    children: React.ReactNode;
    rootMargin?: string;
    placeholder?: React.ReactNode;
};

const InViewMount = ({ children, rootMargin = "200px", placeholder = null }: InViewMountProps) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (isVisible) return () => { /* ignore */ };

        const element = ref.current;
        if (!element) return  () => { /* ignore */ };

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin }
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [isVisible, rootMargin]);

    return <div ref={ref}>{isVisible ? children : placeholder}</div>;
};

const CardLayout = <T extends IdNameItem>({
    cards,
    Component
}: CardLayoutProps<T>) => (
    <ul role="list" className="grid gap-y-6 grid-cols-[repeat(auto-fill,24rem)] justify-around">
        {cards.map((card, index) => (
            <li key={card.id || index.toString()}>
                <InViewMount placeholder={
                    <Card additionalClasses="h-88 w-82 flex"> {/* Taille actuelle de la carte */}
                        <div className='h-10 border-b border-slate-300' />

                        <div className='flex flex-col justify-center items-center gap-2 w-full h-full pt-2'>
                            <div className='truncate text-center font-semibold' title={card.name}>
                                {card.name}
                            </div>
                            <SimpleLoadingIndicator/>
                        </div>
                    </Card>
                }>
                    <Component {...card} />
                </InViewMount>
            </li>
        ))}
    </ul>
);

export default CardLayout;
